import React, { useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import { HeartPulse } from 'lucide-react';
import type { IWellnessDashboardProps } from '../../interfaces';
import { EmptyState, ParticipantPagination } from '../components';
import { WellnessAssessmentModal, WellnessInviteCard } from './components';

/**
 * Wellness tab composition root.
 * Data + pagination come from props (owned by the parent hook);
 * this component only manages the selected-invite state for the modal.
 */
export const WellnessDashboard = ({
  invites,
  totalCount,
  loading,
  page,
  pageSize,
  onPageChange,
  onRefresh,
}: IWellnessDashboardProps) => {
  const [selectedInviteId, setSelectedInviteId] = useState<string | null>(null);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={220} sx={{ borderRadius: 2.5 }} />
        ))}
      </Box>
    );
  }

  if (invites.length === 0) {
    return (
      <EmptyState
        icon={HeartPulse}
        title="No Wellness Assessments"
        subtitle="Wellness screenings you are invited to will appear here."
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {invites.map((invite) => (
          <WellnessInviteCard
            key={invite.inviteId}
            invite={invite}
            onClick={() => setSelectedInviteId(invite.inviteId)}
          />
        ))}
      </Box>

      <ParticipantPagination
        page={page}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={onPageChange}
      />

      {selectedInviteId && (
        <WellnessAssessmentModal
          inviteId={selectedInviteId}
          isOpen={selectedInviteId !== null}
          onClose={() => {
            setSelectedInviteId(null);
            onRefresh?.();
          }}
          onProgress={onRefresh}
        />
      )}
    </>
  );
};
